import React, { useState } from "react";

export type PulsePreferences = {
  display_name?: string;
  voice_profile?: string;
  bio?: string;
  hero_url?: string;
  headshot_url?: string;
  logo_url?: string;
  accent?: string;
  text_scale?: number;
  tracked_metrics?: string[];
  creator_segment?: "building" | "established";
  setup_goals?: string[];
  media_kit_email?: string;
  media_kit_tags?: string[];
  visible_profile?: boolean;
  visible_bio_intro?: boolean;
  visible_metrics_subscribers?: boolean;
  visible_metrics_avg_views?: boolean;
  visible_metrics_engagement?: boolean;
  visible_metrics_demographics?: boolean;
  visible_iris_partnership_fit?: boolean;
};

export type PulseRange = { lookback_days: number; start_date?: string; end_date?: string };
export const PULSE_RANGE_DEFAULT_DAYS = 30;
// Server hard-caps lookback_days at 365 (POST /api/metrics/all rejects
// anything above it with found:false/metrics_computation_failed) - "All"
// means "as far back as the backend will compute," not literal lifetime.
export const PULSE_RANGE_ALL_DAYS = 365;
const RANGE_PRESETS: Array<{ key: string; label: string; days: number }> = [
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: PULSE_RANGE_DEFAULT_DAYS },
  { key: "3mo", label: "3 months", days: 90 },
  { key: "all", label: "All", days: PULSE_RANGE_ALL_DAYS },
];
export const RangePicker: React.FC<{ value: PulseRange; onChange: (value: PulseRange) => void }> = ({ value, onChange }) => {
  const [custom, setCustom] = useState(Boolean(value.start_date));
  const [start, setStart] = useState(value.start_date || "");
  const [end, setEnd] = useState(value.end_date || new Date().toISOString().slice(0, 10));
  const activePreset = custom ? null : RANGE_PRESETS.find((preset) => preset.days === value.lookback_days)?.key || null;
  const selectPreset = (days: number) => {
    setCustom(false);
    onChange({ lookback_days: days });
  };
  return (
    <div className="pulse-range-picker">
      <button
        type="button"
        className="pulse-range-default"
        aria-label="Reset to default reporting period"
        onClick={() => selectPreset(PULSE_RANGE_DEFAULT_DAYS)}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <rect x="2" y="2" width="7" height="7" rx="1.5" fill="currentColor" />
          <rect x="11" y="2" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.5" />
          <rect x="2" y="11" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.5" />
          <rect x="11" y="11" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.5" />
        </svg>
        Default
      </button>
      <div className="pulse-range-segmented" role="tablist" aria-label="Reporting period">
        {RANGE_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            role="tab"
            aria-selected={activePreset === preset.key}
            className={activePreset === preset.key ? "active" : ""}
            onClick={() => selectPreset(preset.days)}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={custom}
          className={custom ? "active" : ""}
          onClick={() => setCustom(true)}
        >
          Custom
        </button>
      </div>
      {custom && (
        <div className="pulse-range-custom">
          <label>
            From
            <input type="date" aria-label="Range start" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" aria-label="Range end" value={end} min={start} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setEnd(e.target.value)} />
          </label>
          <button
            type="button"
            disabled={!start || !end || start > end || (Date.parse(end) - Date.parse(start)) / 86400000 > 365}
            onClick={() => onChange({ lookback_days: PULSE_RANGE_DEFAULT_DAYS, start_date: start, end_date: end })}
          >
            Apply range
          </button>
        </div>
      )}
    </div>
  );
};

export const PreferenceEditor: React.FC<{
  value: PulsePreferences;
  onSave: (value: PulsePreferences) => Promise<void>;
  branding?: boolean;
}> = ({ value, onSave, branding }) => {
  const [draft, setDraft] = useState(value);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const field = (key: keyof PulsePreferences, label: string, type = "text") => <label key={key}>{label}<input type={type} value={String(draft[key] || "")} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} /></label>;
  return <form className="pulse-v2-card pulse-review-preferences" onSubmit={async (e) => {
    e.preventDefault(); setBusy(true); setMessage("");
    try { await onSave(draft); setMessage("Changes saved."); } catch { setMessage("Could not save changes. Please retry."); } finally { setBusy(false); }
  }}>
    <h2>{branding ? "Brand your media kit" : "Reading and IRIS preferences"}</h2>
    {branding ? <>{field("display_name", "Display name")}{field("hero_url", "Hero image URL", "url")}{field("headshot_url", "Headshot URL", "url")}{field("logo_url", "Logo URL", "url")}
      <label>Brand color<input type="color" value={draft.accent || "#8428f5"} onChange={(e) => setDraft({ ...draft, accent: e.target.value })} /></label>
      <label>Bio<textarea rows={4} value={draft.bio || ""} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} /></label>
      <p>Use HTTPS links for your images. These changes are saved for this channel and included in your export.</p></> : <>
      <label>Text size<select value={draft.text_scale || 1} onChange={(e) => setDraft({ ...draft, text_scale: Number(e.target.value) })}><option value="1">Standard</option><option value="1.2">Larger</option><option value="1.4">Largest</option></select></label>
      <label>Creator journey<select value={draft.creator_segment || "building"} onChange={(e) => setDraft({ ...draft, creator_segment: e.target.value as "building" | "established" })}><option value="building">Building my audience</option><option value="established">Established creator</option></select></label>
      <label>IRIS voice profile<textarea rows={5} placeholder="Describe the tone, wording, and examples IRIS should follow." value={draft.voice_profile || ""} onChange={(e) => setDraft({ ...draft, voice_profile: e.target.value })} /></label>
      <p>Your saved voice profile is applied to future IRIS replies.</p></>}
    <button type="submit" className="pulse-v2-primary" disabled={busy}>{busy ? "Saving..." : "Save changes"}</button><p role="status">{message}</p>
  </form>;
};
